import React from 'react';

import { Icon } from '@/nova/components';
import { LP_POSTER } from '@/nova/theme/tokens';

import { liveFragmentDocument, withInertLinks } from '../../landing-page-api';
import { PreviewFrame } from '../PlayableComposer/PreviewFrame';
import * as S from './LivePagePoster.styles';

export interface LivePagePosterProps {
  /** A live page's `full_html` fragment ('' when it has none: the placeholder stays). */
  html?: string;
  /** Reads a complete preview document once the card is (nearly) on screen (a saved draft's page); null keeps the
   *  placeholder. Read once per mounted poster. */
  loadDoc?: () => Promise<string | null>;
  /** Names the document `loadDoc` reads (the page's version and its content): a document read once this session is
   *  drawn again without reading it. Change it (and remount) when the content changes. */
  cacheKey?: string;
  /** Key art shown under the page until it has loaded (and in its place when it cannot be drawn). */
  imageUrl?: string;
  /** Translated name of the frame (the frame is inert and hidden from assistive tech: the card names itself). */
  title: string;
  /** `desktop` (default): a desktop viewport's first screen in a landscape frame. `mobile`: a phone's first screen,
   *  filling the parent's (portrait) frame. `screen`: a full-screen game, laid out at a real phone viewport and its
   *  whole screen fitted (letterboxed) into the parent's frame, so the game is never cut. */
  layout?: 'desktop' | 'mobile' | 'screen';
}

/** The CSS size a page is laid out at in its poster: a desktop viewport's first screen (16:10, the landscape frame's
 *  ratio), a phone's (4:5, a draft card's portrait frame), or a whole phone screen (a full-screen game fills exactly
 *  one). Fixed heights: deriving them from the measured box would wobble by a pixel between widths, and every change
 *  remounts the page. Layout. */
const DOC_SIZE = {
  desktop: { w: 1280, h: 800 },
  mobile: { w: 390, h: 488 },
  screen: { w: 390, h: 844 },
} as const;

/** Which part of a cropped phone screen's overflow is cut from the top (0 = top-aligned, .5 = centred). Layout. */
const SCREEN_CROP = 0.32;

/** A poster's page never shows a scrollbar, on any element (the card is a picture of the page, not a window onto
 *  it), and its frame's canvas takes the page's own background (a page whose html is transparent would otherwise
 *  show the frame's white canvas wherever its content does not reach). */
const NO_SCROLLBARS =
  '<style>html,body{overflow:hidden!important}*{scrollbar-width:none!important}' +
  '*::-webkit-scrollbar{display:none!important;width:0!important;height:0!important}</style>' +
  '<script>addEventListener("DOMContentLoaded",function(){var d=document.documentElement,b=document.body;' +
  'function bg(e){if(!e)return "";var s=getComputedStyle(e),c=s.backgroundColor;' +
  'if(c&&c!=="transparent"&&!/,\\s*0\\)$/.test(c))return c;return s.backgroundImage&&s.backgroundImage!=="none"?s.background:"";}' +
  'var c=bg(b),k=b?b.children:[];for(var i=0;!c&&i<k.length;i++){if(!/^(LINK|STYLE|SCRIPT|META|NOSCRIPT)$/.test(k[i].tagName))c=bg(k[i]);}' +
  'if(c&&!bg(d))d.style.background=c;});</script>';

/**
 * Makes a poster's page a still picture of itself: the list shows many at once, and each running live cost a whole
 * page's animations, timers and decoded art. Injected first in the page's head, ahead of its own scripts, it
 * - marks the document `data-lp-static`, so a landing page's runtime skips its loops and the art of states a still
 *   picture never shows (other languages, prizes, the win window);
 * - snaps every CSS animation and transition to its resting frame and drops the compositor hints;
 * - lets the page's scripts run while it loads and settles (its layout passes), then cancels every timer, animation
 *   frame and idle callback it asked for and stops every script animation; after that, a loop (a frame or timer
 *   asked for from inside one) is refused, while a one-off asked for by an event (an image that loads late and is
 *   laid out) still runs. Videos never play.
 * Generic: it holds for pages made outside the Studio too.
 */
const STATIC_PRELUDE =
  `<style>*,*::before,*::after{animation-duration:${LP_POSTER.FREEZE}!important;animation-delay:${LP_POSTER.FREEZE}!important;` +
  `animation-iteration-count:1!important;transition-duration:${LP_POSTER.FREEZE}!important;transition-delay:${LP_POSTER.FREEZE}!important;` +
  'will-change:auto!important}</style>' +
  '<script>(function(){var W=window,D=document;D.documentElement.setAttribute("data-lp-static","");' +
  'var sT=W.setTimeout,cT=W.clearTimeout,sI=W.setInterval,cI=W.clearInterval,rA=W.requestAnimationFrame,' +
  'cA=W.cancelAnimationFrame,rI=W.requestIdleCallback,cX=W.cancelIdleCallback,T=[],I=[],A=[],Q=[],still=0,depth=0;' +
  'function guard(f){return typeof f!=="function"?f:function(){depth++;try{return f.apply(this,arguments)}' +
  'finally{depth--}}}' +
  'function wrap(f,list,always){return function(){if(still&&(always||depth))return 0;' +
  'var a=Array.prototype.slice.call(arguments);a[0]=guard(a[0]);var id=f.apply(W,a);if(!still)list.push(id);' +
  'return id}}' +
  'W.setTimeout=wrap(sT,T,0);W.setInterval=wrap(sI,I,1);W.requestAnimationFrame=wrap(rA,A,0);' +
  'if(rI)W.requestIdleCallback=wrap(rI,Q,0);' +
  'function videos(){var v=D.getElementsByTagName("video");for(var i=0;i<v.length;i++){v[i].autoplay=false;' +
  'v[i].removeAttribute("autoplay");try{v[i].pause()}catch(e){}}}' +
  'function freeze(){if(still)return;still=1;videos();' +
  'T.forEach(function(i){cT.call(W,i)});I.forEach(function(i){cI.call(W,i)});' +
  'A.forEach(function(i){cA.call(W,i)});if(cX)Q.forEach(function(i){cX.call(W,i)});T=I=A=Q=[];' +
  'if(D.getAnimations)D.getAnimations().forEach(function(n){try{n.finish()}catch(e){n.cancel()}})}' +
  'D.addEventListener("DOMContentLoaded",videos);' +
  'W.addEventListener("load",function(){sT.call(W,function(){rA.call(W,function(){rA.call(W,freeze)})},' +
  `${LP_POSTER.SETTLE_MS})});sT.call(W,freeze,${LP_POSTER.FREEZE_MAX_MS})})();</script>`;

/** Extra CSS px a poster's page is drawn wider than its card, cropped evenly at both sides, so no sub-pixel strip
 *  can open at an edge whatever the card's fractional width. Layout. */
const OVERSCAN = 2;

/** The document as a poster draws it: {@link STATIC_PRELUDE} and {@link NO_SCROLLBARS} at the top of its head (or
 *  in front, without one). Pure. */
const posterDocument = (doc: string): string => {
  const prelude = STATIC_PRELUDE + NO_SCROLLBARS;
  const head = /<head(\s[^>]*)?>/i.exec(doc);
  if (!head) return prelude + doc;
  const at = head.index + head[0].length;
  return doc.slice(0, at) + prelude + doc.slice(at);
};

/** The animated Whitelabels.com loader (the one the app boots with and the configurator's previews show). */
const POSTER_LOADER_SRC = 'https://micros-t.b-cdn.net/whitelabels.com-loader-1775466124392.svg';

/** How far ahead of the viewport a card starts loading its page (ready as it scrolls in). Layout. */
const LOAD_MARGIN = '25% 0px';

/** How far from the viewport a loaded card's page is kept: one further away is dropped, freeing its decoded art, and
 *  drawn again from its held document when it comes back. Layout. */
const KEEP_MARGIN = '100% 0px';

/** How many posters read and load their page at once; the rest wait their turn, the one nearest the screen first (a
 *  long list on a phone would otherwise download and decode every card's art in one burst). */
const LOADING_AT_ONCE = 3;

/** Poster documents read this session, by their `cacheKey`, so returning to the list draws them without asking
 *  spark-creatives again. Bounded: the least recently read is dropped first. */
const DOC_CACHE_SIZE = 24;
const docCache = new Map<string, string>();
const cacheDoc = (key: string, doc: string): void => {
  docCache.delete(key);
  docCache.set(key, doc);
  while (docCache.size > DOC_CACHE_SIZE) {
    const oldest = docCache.keys().next();
    if (oldest.done) return;
    docCache.delete(oldest.value);
  }
};

/** A poster waiting for, or holding, a loading turn. */
interface Turn {
  grant: () => void;
  /** How far the card is from the screen right now (0 on screen). */
  distance: () => number;
  granted: boolean;
  done: boolean;
}
let turnsHeld = 0;
const turnQueue: Turn[] = [];
const grantTurns = (): void => {
  while (turnsHeld < LOADING_AT_ONCE && turnQueue.length > 0) {
    // The waiting card nearest the screen goes first (the list may have scrolled since they queued).
    let at = 0;
    let best = Infinity;
    turnQueue.forEach((turn, i) => {
      const d = turn.distance();
      if (d < best) {
        best = d;
        at = i;
      }
    });
    const [next] = turnQueue.splice(at, 1);
    if (!next) return;
    turnsHeld += 1;
    next.granted = true;
    next.grant();
  }
};

/** How far an element is from the viewport, in CSS px (0 when any of it is on screen). */
const viewportDistance = (el: Element | null): number => {
  if (!el || typeof window === 'undefined') return Infinity;
  const r = el.getBoundingClientRect();
  return Math.max(0, r.top - window.innerHeight, -r.bottom);
};

/** Queues a poster for a loading turn (`grant` runs when it is its turn). Returns the release, safe to call twice:
 *  it gives the turn back, or leaves the queue. */
const takeTurn = (grant: () => void, distance: () => number): (() => void) => {
  const turn: Turn = { grant, distance, granted: false, done: false };
  turnQueue.push(turn);
  grantTurns();
  return () => {
    if (turn.done) return;
    turn.done = true;
    if (turn.granted) {
      turnsHeld -= 1;
      grantTurns();
      return;
    }
    const at = turnQueue.indexOf(turn);
    if (at >= 0) turnQueue.splice(at, 1);
  };
};

interface Box {
  w: number;
  h: number;
}

/** Marks the page layer inert (no focus, no assistive tech inside the frame); React 18 has no `inert` prop. */
const inertRef = (el: HTMLDivElement | null): void => {
  el?.setAttribute('inert', '');
};

/**
 * A landing page's card poster: the page drawn from its own HTML (a live page's fragment, or a draft's preview read
 * once it nears the screen) at a desktop or phone width and scaled to the card, in a frame sandboxed to scripts only
 * (no same-origin) with its links made inert. It never loads the live URL, which would count as a real visit. The
 * page is a still picture of itself ({@link STATIC_PRELUDE}), loads in turn with the other posters, and is dropped
 * while the card is far off screen. The frame fades in over a placeholder (the key art when there is one) once
 * loaded.
 */
export const LivePagePoster = ({
  html = '',
  loadDoc,
  cacheKey,
  imageUrl,
  title,
  layout = 'desktop',
}: LivePagePosterProps): React.ReactElement => {
  const boxRef = React.useRef<HTMLDivElement>(null);
  // Close to the screen: the page loads (`soon`); within a screen of it: a loaded page is kept (`near`).
  const [soon, setSoon] = React.useState(false);
  const [near, setNear] = React.useState(false);
  const [turn, setTurn] = React.useState(false);
  const [box, setBox] = React.useState<Box | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [readDoc, setReadDoc] = React.useState<string | null>(() => {
    const held = cacheKey ? docCache.get(cacheKey) : undefined;
    return held ? withInertLinks(held) : null;
  });
  // The page could not be read: the stand-in art becomes the poster (sharp, no loader).
  const [failed, setFailed] = React.useState(false);
  const size = DOC_SIZE[layout];
  const drawable = Boolean(html) || Boolean(loadDoc);

  // Syncs with the viewport: whether the card is close to the screen (its page loads) and within a screen of it (a
  // loaded page is kept; further away it is dropped). Without an IntersectionObserver (an old engine) it is always
  // both.
  React.useEffect(() => {
    if (!drawable) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setSoon(true);
      setNear(true);
      return undefined;
    }
    const el = boxRef.current;
    if (!el) return undefined;
    const last = (entries: IntersectionObserverEntry[]): IntersectionObserverEntry | undefined =>
      entries[entries.length - 1];
    const load = new IntersectionObserver(
      (entries) => {
        const entry = last(entries);
        if (entry) setSoon(entry.isIntersecting);
      },
      { rootMargin: LOAD_MARGIN },
    );
    const keep = new IntersectionObserver(
      (entries) => {
        const entry = last(entries);
        if (!entry) return;
        setNear(entry.isIntersecting);
        // A dropped page loads afresh when it comes back.
        if (!entry.isIntersecting) setLoaded(false);
      },
      { rootMargin: KEEP_MARGIN },
    );
    load.observe(el);
    keep.observe(el);
    return () => {
      load.disconnect();
      keep.disconnect();
    };
  }, [drawable]);

  // Syncs with the other posters: a card close to the screen whose page has not loaded waits for a loading turn, and
  // gives it back once its page has loaded or failed, or when it moves away.
  React.useEffect(() => {
    if (!drawable || !soon || loaded || failed) return undefined;
    const release = takeTurn(
      () => setTurn(true),
      () => viewportDistance(boxRef.current),
    );
    return () => {
      release();
      setTurn(false);
    };
  }, [drawable, soon, loaded, failed]);

  // Syncs with the preview service: a draft's page is read on its first turn (the reader is read through a ref, so a
  // new identity per render does not read it again), and held for the session under its key.
  const loadRef = React.useRef(loadDoc);
  loadRef.current = loadDoc;
  React.useEffect(() => {
    if (!turn || html || readDoc !== null || !loadRef.current) return undefined;
    let live = true;
    void loadRef.current().then(
      (doc) => {
        if (!live) return;
        if (!doc) {
          setFailed(true);
          return;
        }
        if (cacheKey) cacheDoc(cacheKey, doc);
        setReadDoc(withInertLinks(doc));
      },
      () => {
        if (live) setFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, [turn, html, readDoc, cacheKey]);

  const doc = React.useMemo(() => {
    const d = html ? liveFragmentDocument(html, title) : readDoc;
    return d ? posterDocument(d) : null;
  }, [html, title, readDoc]);

  // Syncs with the layout: the poster's size, which the page frame is scaled to.
  React.useEffect(() => {
    const el = boxRef.current;
    if (!el || !doc) return undefined;
    const read = (): void =>
      setBox((cur) =>
        cur && cur.w === el.clientWidth && cur.h === el.clientHeight ? cur : { w: el.clientWidth, h: el.clientHeight },
      );
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [doc]);

  // Every poster fills the card's width. A whole phone screen (a full-screen game) is taller than the card: it is
  // cropped, keeping the upper part of the overflow (the logo, the offer and the game stay in view).
  const scale = box && box.w > 0 ? (box.w + OVERSCAN) / size.w : 0;
  const shift = layout === 'screen' && box ? Math.max(0, size.h * scale - box.h) * SCREEN_CROP : 0;
  // The page mounts on its turn and stays while the card is within a screen of the viewport.
  const mounted = Boolean(doc && box && scale > 0 && near && (turn || loaded));
  // The page is on its way (the first time, or again after it was dropped far off screen): the stand-in art is
  // blurred out under the animated loader until it has loaded.
  const waiting = drawable && !loaded && !failed;
  return (
    <S.PosterBox ref={boxRef} $fill={layout !== 'desktop'}>
      {imageUrl ? (
        <S.PlaceholderImg src={imageUrl} alt="" loading="lazy" $waiting={waiting} />
      ) : (
        <S.Placeholder>
          <Icon name="globe" size={22} />
          {drawable ? null : <S.PlaceholderText text="No preview for this page" />}
        </S.Placeholder>
      )}
      {drawable ? <S.Loader src={POSTER_LOADER_SRC} alt="" aria-hidden="true" $shown={waiting} /> : null}
      {mounted && doc ? (
        <S.FrameLayer ref={inertRef} aria-hidden="true" $shown={loaded}>
          <S.FrameShift $shift={shift} $inset={OVERSCAN / 2}>
            <PreviewFrame html={doc} w={size.w} h={size.h} scale={scale} title={title} onLoad={() => setLoaded(true)} />
          </S.FrameShift>
        </S.FrameLayer>
      ) : null}
    </S.PosterBox>
  );
};

export default LivePagePoster;
